import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { money } from "@/lib/format";
import { SortHeader, useSort } from "@/components/SortHeader";
import { Search, Calculator, Package, Truck, Loader2, Check, Layers } from "lucide-react";
import { toast } from "sonner";

const EMPTY = {
  unit_price_eur: 0, units_per_box: 1, boxes_per_pallet: 1, pallets_per_truck: 1,
  navlun: 0, gumruk: 0, mediu: 0, ic_nakliye: 0, depolama: 0, sigorta: 0, margin_pct: 20,
};

function computeLocal(cfg, eurRate) {
  const n = (v) => Number(v) || 0;
  const ub = n(cfg.units_per_box), bp = n(cfg.boxes_per_pallet), pt = n(cfg.pallets_per_truck);
  const upt = ub * bp * pt;
  const unitRon = n(cfg.unit_price_eur) * eurRate;
  const extras = ["navlun", "gumruk", "mediu", "ic_nakliye", "depolama", "sigorta"].reduce((a, k) => a + n(cfg[k]), 0);
  const goods = unitRon * upt;
  const total = goods + extras;
  const cpu = upt > 0 ? total / upt : unitRon;
  const sell = cpu * (1 + n(cfg.margin_pct) / 100);
  return {
    units_per_truck: upt, unit_price_ron: unitRon, extras_ron: extras, goods_truck_ron: goods,
    total_truck_ron: total, cost_per_unit_ron: cpu, cost_per_box_ron: cpu * ub, cost_per_pallet_ron: cpu * ub * bp,
    sell_unit_ron: sell, sell_unit_with_tva_ron: sell * 1.21,
  };
}

const COST_ROWS = [
  ["navlun", "Navlun (tır) ücreti"],
  ["gumruk", "Gümrük masrafları"],
  ["mediu", "Mediu (çevre vergisi)"],
  ["ic_nakliye", "İç nakliye ücreti"],
  ["depolama", "Depolama & palet yapma"],
  ["sigorta", "Sigorta ücreti"],
];

function Field({ label, value, onChange, suffix, testid }) {
  return (
    <div>
      <label className="mb-1 block text-xs text-muted-foreground">{label}</label>
      <div className="relative">
        <Input data-testid={testid} type="number" value={value} onChange={(e) => onChange(e.target.value)} className="h-9 pr-12 font-mono" />
        {suffix && <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">{suffix}</span>}
      </div>
    </div>
  );
}

export default function Cost() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [cat, setCat] = useState("mamul");
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(null); // {stock, cfg, eurRate}
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const d = await api.listCosts(search, 0, 60, cat === "all" ? undefined : cat);
      setItems(d.items);
      setTotal(d.total);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, cat]);

  const openCard = async (row) => {
    const d = await api.getCost(row.id);
    setActive({ stock: d.stock, cfg: { ...EMPTY, ...d.config }, eurRate: d.computed.eur_rate, salePrice: d.stock.sale_price_net ? String(d.stock.sale_price_net) : "" });
    setOpen(true);
  };

  const setField = (k, v) => setActive((a) => ({ ...a, cfg: { ...a.cfg, [k]: v } }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...active.cfg };
      if (active.salePrice !== "" && active.salePrice != null) payload.sale_price_net = parseFloat(active.salePrice) || 0;
      await api.saveCost(active.stock.id, payload);
      toast.success("Maliyet ve satış fiyatı kaydedildi");
      setOpen(false);
      load();
    } catch {
      toast.error("Kaydedilemedi");
    } finally {
      setSaving(false);
    }
  };

  const comp = active ? computeLocal(active.cfg, active.eurRate) : null;

  const costSort = useSort(items, {
    name: (s) => s.name || "",
    code: (s) => s.code || "",
    cost_per_unit_ron: (s) => s.cost_per_unit_ron ?? -Infinity,
    sale: (s) => (s.sale_price_net > 0 ? s.sale_price_net : s.sell_unit_ron) ?? -Infinity,
    sale_vat: (s) => (s.sale_price_vat > 0 ? s.sale_price_vat : s.sell_unit_with_tva_ron) ?? -Infinity,
    configured: (s) => (s.configured ? 1 : 0),
  });
  const sortedItems = costSort.sorted;

  return (
    <div className="p-6 lg:p-8">
      <PageHeader title="Maliyet" subtitle="Ürün başına ithalat maliyeti · 1 tır senaryosu · birim fiyat EUR, masraflar lei" />

      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-2">
          {[["mamul", "Mamül", Package], ["hammadde", "Hammadde", Layers], ["all", "Tümü", null]].map(([k, l, Icon]) => (
            <button key={k} data-testid={`cost-tab-${k}`} onClick={() => setCat(k)} className={`flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-xs font-medium transition-colors ${cat === k ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground hover:text-foreground"}`}>
              {Icon && <Icon className="h-3.5 w-3.5" />} {l}
            </button>
          ))}
        </div>
        <div className="relative w-full sm:w-96">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input data-testid="cost-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Ürün adı veya kodu ara..." className="pl-9" />
        </div>
      </div>

      <div className="overflow-hidden rounded-xl border border-border bg-card">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border text-left text-xs uppercase tracking-wider text-muted-foreground">
              <SortHeader label="Ürün" sortId="name" sort={costSort} />
              <SortHeader label="Kod" sortId="code" sort={costSort} />
              <SortHeader label="Birim Maliyet" sortId="cost_per_unit_ron" sort={costSort} align="right" />
              <SortHeader label="Satış Fiyatı" sortId="sale" sort={costSort} align="right" />
              <SortHeader label="+%21 TVA" sortId="sale_vat" sort={costSort} align="right" />
              <th className="px-5 py-3.5 text-right font-semibold">İşlem</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={6} className="px-5 py-12 text-center text-muted-foreground"><Loader2 className="mx-auto h-5 w-5 animate-spin" /></td></tr>}
            {!loading && items.length === 0 && <tr><td colSpan={6} className="px-5 py-12 text-center text-muted-foreground">Kayıt bulunamadı</td></tr>}
            {!loading && sortedItems.map((s) => (
              <tr key={s.id} data-testid="cost-row" className="border-b border-border/50 hover:bg-secondary/30">
                <td className="px-5 py-3 font-medium">{s.name}</td>
                <td className="px-5 py-3 font-mono text-xs text-teal-300">{s.code || "-"}</td>
                <td className="px-5 py-3 text-right font-mono">{s.configured ? money(s.cost_per_unit_ron) : <span className="text-muted-foreground">—</span>}</td>
                <td className="px-5 py-3 text-right font-mono text-emerald-400">{s.sale_price_net > 0 ? money(s.sale_price_net) : s.configured ? money(s.sell_unit_ron) : <span className="text-muted-foreground">—</span>}</td>
                <td className="px-5 py-3 text-right font-mono text-amber-400">{s.sale_price_vat > 0 ? money(s.sale_price_vat) : s.configured ? money(s.sell_unit_with_tva_ron) : <span className="text-muted-foreground">—</span>}</td>
                <td className="px-5 py-3 text-right">
                  <Button data-testid="cost-configure-btn" size="sm" variant="outline" onClick={() => openCard(s)} className="gap-1.5 text-xs">
                    <Calculator className="h-3.5 w-3.5" /> {s.configured ? "Düzenle" : "Yapılandır"}
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="mt-2 text-xs text-muted-foreground">Toplam {total} ürün {search ? "(filtreli)" : "· en çok satılan 60 gösteriliyor, aramayla daraltın"}</p>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto">
          {active && (
            <>
              <DialogHeader>
                <DialogTitle className="pr-8 text-base">{active.stock.name}</DialogTitle>
                {active.stock.code && <span className="font-mono text-xs text-teal-300">Kod: {active.stock.code}</span>}
              </DialogHeader>

              <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                <div className="space-y-4">
                  <div className="flex items-center gap-2 text-sm font-medium text-primary"><Package className="h-4 w-4" /> Paketleme</div>
                  <div className="grid grid-cols-3 gap-2">
                    <Field label="1 koli = adet" value={active.cfg.units_per_box} onChange={(v) => setField("units_per_box", v)} testid="cost-units-box" />
                    <Field label="1 palet = koli" value={active.cfg.boxes_per_pallet} onChange={(v) => setField("boxes_per_pallet", v)} testid="cost-boxes-pallet" />
                    <Field label="1 tır = palet" value={active.cfg.pallets_per_truck} onChange={(v) => setField("pallets_per_truck", v)} testid="cost-pallets-truck" />
                  </div>
                  <div className="rounded-lg bg-secondary/40 px-3 py-2 text-xs text-muted-foreground">
                    1 tır = <span className="font-mono font-semibold text-foreground">{comp.units_per_truck.toLocaleString("tr-TR")}</span> adet
                  </div>

                  <Field label="1 adet ürün alış fiyatı (EUR)" value={active.cfg.unit_price_eur} onChange={(v) => setField("unit_price_eur", v)} suffix="€" testid="cost-unit-eur" />
                  <div className="rounded-lg bg-secondary/40 px-3 py-2 text-xs text-muted-foreground">
                    ≈ <span className="font-mono font-semibold text-foreground">{money(comp.unit_price_ron)}</span> (kur {active.eurRate})
                  </div>

                  <div className="flex items-center gap-2 pt-1 text-sm font-medium text-primary"><Truck className="h-4 w-4" /> Tır Masrafları (lei)</div>
                  <div className="grid grid-cols-2 gap-2">
                    {COST_ROWS.map(([k, lbl]) => (
                      <Field key={k} label={lbl} value={active.cfg[k]} onChange={(v) => setField(k, v)} suffix="lei" testid={`cost-${k}`} />
                    ))}
                  </div>

                  <Field label="Kâr marjı (%)" value={active.cfg.margin_pct} onChange={(v) => setField("margin_pct", v)} suffix="%" testid="cost-margin" />

                  <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-3">
                    <Field label="Satış Fiyatı (net, elle gir — tüm menüleri günceller)" value={active.salePrice} onChange={(v) => setActive((a) => ({ ...a, salePrice: v }))} suffix="lei" testid="cost-sale-price-input" />
                    <p className="mt-1.5 text-[11px] text-muted-foreground">Girdiğiniz satış fiyatı Ürün Listesi, Stok ve raporlardaki satış rakamlarını da günceller. Boş bırakırsanız marjdan hesaplanan fiyat kullanılır.</p>
                  </div>
                </div>

                <div className="space-y-2 rounded-xl border border-border bg-secondary/20 p-4">
                  <div className="mb-2 text-sm font-medium">Maliyet Dökümü (1 tır)</div>
                  {[
                    ["Mal bedeli (tır)", comp.goods_truck_ron],
                    ["Masraflar toplamı", comp.extras_ron],
                    ["TOPLAM tır maliyeti", comp.total_truck_ron],
                  ].map(([l, v]) => (
                    <div key={l} className="flex justify-between text-sm"><span className="text-muted-foreground">{l}</span><span className="font-mono">{money(v)}</span></div>
                  ))}
                  <div className="my-2 h-px bg-border" />
                  {[
                    ["Birim maliyet (1 adet)", comp.cost_per_unit_ron],
                    ["Koli maliyeti", comp.cost_per_box_ron],
                    ["Palet maliyeti", comp.cost_per_pallet_ron],
                  ].map(([l, v]) => (
                    <div key={l} className="flex justify-between text-sm"><span className="text-muted-foreground">{l}</span><span className="font-mono">{money(v)}</span></div>
                  ))}
                  <div className="my-2 h-px bg-border" />
                  <div className="flex justify-between text-sm"><span className="text-muted-foreground">Kâr marjı</span><span className="font-mono">%{Number(active.cfg.margin_pct) || 0}</span></div>
                  <div className="flex justify-between"><span className="font-medium text-emerald-400">Satış fiyatı (birim)</span><span data-testid="cost-sell-price" className="font-mono font-semibold text-emerald-400">{money(comp.sell_unit_ron)}</span></div>
                  <div className="mt-2 rounded-lg bg-amber-500/10 px-3 py-2">
                    <div className="flex justify-between text-sm"><span className="text-amber-300">%21 TVA dahil satış (bilgi)</span><span className="font-mono font-semibold text-amber-300">{money(comp.sell_unit_with_tva_ron)}</span></div>
                  </div>
                  {(active.stock.purchase_price || active.stock.sale_price_net) && (
                    <div className="mt-2 space-y-1 rounded-lg border border-border bg-secondary/30 px-3 py-2">
                      <div className="text-xs font-medium text-muted-foreground">Ürün Listesi Fiyatları (lei)</div>
                      <div className="flex justify-between text-sm"><span className="text-muted-foreground">Alış tutarı</span><span className="font-mono text-amber-300">{active.stock.purchase_price ? money(active.stock.purchase_price) : "-"}</span></div>
                      <div className="flex justify-between text-sm"><span className="text-muted-foreground">TVA'sız satış</span><span className="font-mono">{active.stock.sale_price_net ? money(active.stock.sale_price_net) : "-"}</span></div>
                      {active.stock.purchase_price && active.stock.sale_price_net ? (
                        <div className="flex justify-between text-sm"><span className="text-muted-foreground">Kâr marjı</span><span data-testid="cost-product-margin" className={`font-mono font-semibold ${active.stock.sale_price_net - active.stock.purchase_price >= 0 ? "text-emerald-400" : "text-rose-400"}`}>{money(active.stock.sale_price_net - active.stock.purchase_price)} (%{(((active.stock.sale_price_net - active.stock.purchase_price) / active.stock.purchase_price) * 100).toFixed(1)})</span></div>
                      ) : null}
                    </div>
                  )}
                </div>
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)}>İptal</Button>
                <Button data-testid="cost-save-btn" onClick={save} disabled={saving} className="gap-1.5">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} Kaydet
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
